// The React half of the inset registry (PLAN-UI §28.1).
//
// **All the geometry is in `insets.ts` and none of it is here.** This file owns
// three things only: measuring a DOM element, remembering the rects, and telling
// React when they moved. Every answer is delegated — a subtraction written at a
// call site is a second definition of the free area, and the whole point of the
// registry is that there is one.
//
// Three things read it, and between them they are the whole of what the old
// `layout.ts` answered: the camera's `setViewInset` through `useEdgeInsets`, the
// board-anchored overlays' clip through `useOverlayClip`, and the hint card's
// placement through `useFreeArea`.

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  useSyncExternalStore,
  type ReactNode
} from "react";

import {
  edgeInsets,
  freeAreaOf,
  overlayClipOf,
  NO_INSETS,
  type ChromeRect,
  type Edge,
  type EdgeInsets,
  type Rect,
  type Viewport
} from "./insets";

/** Everything derived from one set of rects, computed once per change. */
export interface InsetSnapshot {
  /** Bumped on every real change — the identity `useSyncExternalStore` compares. */
  version: number;
  viewport: Viewport;
  entries: readonly ChromeRect[];
  insets: EdgeInsets;
  free: Rect;
  clip: string;
}

/**
 * A rect has to move by this much to count as having moved.
 *
 * Sub-pixel jitter is what a laid-out element does — a fractional font metric, a
 * scrollbar, a transform mid-ease — and each report would re-derive the snapshot
 * and wake every consumer. Half a pixel is below anything the camera or a clip
 * can express.
 */
const EPSILON = 0.5;

const same = (a: Rect, b: Rect): boolean =>
  Math.abs(a.x - b.x) < EPSILON &&
  Math.abs(a.y - b.y) < EPSILON &&
  Math.abs(a.width - b.width) < EPSILON &&
  Math.abs(a.height - b.height) < EPSILON;

const EMPTY_SNAPSHOT: InsetSnapshot = {
  version: 0,
  viewport: { width: 0, height: 0 },
  entries: [],
  insets: NO_INSETS,
  free: { x: 0, y: 0, width: 0, height: 0 },
  clip: "inset(0px 0px 0px 0px)"
};

export class InsetStore {
  private rects = new Map<string, ChromeRect>();
  private listeners = new Set<() => void>();
  private viewport: Viewport;
  private snapshot: InsetSnapshot;

  constructor(viewport: Viewport) {
    this.viewport = viewport;
    this.snapshot = this.derive(1);
  }

  subscribe = (listener: () => void): (() => void) => {
    this.listeners.add(listener);
    return () => {
      this.listeners.delete(listener);
    };
  };

  /**
   * The same object until something actually changes.
   *
   * `useSyncExternalStore` re-renders whenever the snapshot's identity moves, so
   * deriving a fresh one per read is an infinite loop rather than a slow path.
   */
  getSnapshot = (): InsetSnapshot => this.snapshot;

  /** Register or move a piece of chrome. Ignored while it has not moved. */
  set(id: string, edge: Edge | null, rect: Rect): void {
    const was = this.rects.get(id);
    if (was && was.edge === edge && same(was.rect, rect)) return;
    this.rects.set(id, { id, edge, rect });
    this.publish();
  }

  /** Unregister — an unmounted piece covers nothing. */
  remove(id: string): void {
    if (this.rects.delete(id)) this.publish();
  }

  setViewport(viewport: Viewport): void {
    if (
      Math.abs(viewport.width - this.viewport.width) < EPSILON &&
      Math.abs(viewport.height - this.viewport.height) < EPSILON
    )
      return;
    this.viewport = viewport;
    this.publish();
  }

  private derive(version: number): InsetSnapshot {
    const entries = [...this.rects.values()];
    const viewport = this.viewport;
    return {
      version,
      viewport,
      entries,
      insets: edgeInsets(entries, viewport),
      free: freeAreaOf(entries, viewport),
      clip: overlayClipOf(entries, viewport)
    };
  }

  private publish(): void {
    this.snapshot = this.derive(this.snapshot.version + 1);
    for (const listener of this.listeners) listener();
  }
}

const StoreContext = createContext<InsetStore | null>(null);

const viewportNow = (): Viewport =>
  typeof window === "undefined"
    ? { width: 0, height: 0 }
    : { width: window.innerWidth, height: window.innerHeight };

export const InsetProvider = ({ children }: { children: ReactNode }) => {
  const store = useMemo(() => new InsetStore(viewportNow()), []);
  useEffect(() => {
    const onResize = () => store.setViewport(viewportNow());
    onResize();
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, [store]);
  return <StoreContext.Provider value={store}>{children}</StoreContext.Provider>;
};

/** The store, or `null` outside a provider — a consumer falls back to nothing. */
export const useInsetStore = (): InsetStore | null => useContext(StoreContext);

// Hoisted, not written inline: `useSyncExternalStore` resubscribes whenever the
// subscribe function's identity moves, and a fresh closure per render is a
// resubscribe per render.
const readEmpty = (): InsetSnapshot => EMPTY_SNAPSHOT;
const subscribeNothing = (): (() => void) => () => {};

const useSnapshot = (): InsetSnapshot => {
  const store = useContext(StoreContext);
  const read = store ? store.getSnapshot : readEmpty;
  const subscribe = store ? store.subscribe : subscribeNothing;
  return useSyncExternalStore(subscribe, read, read);
};

/**
 * Measure this element and keep it registered for as long as it is mounted.
 * Returns a **callback ref** — hand it to the element you want measured.
 *
 * A callback ref rather than a `RefObject` because an effect can only depend on
 * the ref *object*, which never changes, so an element that mounts after the
 * first effect — a conditionally rendered child, the commonest case here — was
 * never measured at all.
 *
 * `getBoundingClientRect` rather than the observer's own box, because an inset is
 * about where the piece *is* on the screen and a `ResizeObserver` entry reports
 * only its size. The window listener is not redundant with the observer for the
 * same reason: an element anchored to the right edge does not change size when
 * the window does, and it has still moved.
 */
export const useChromeRect = (
  id: string,
  edge: Edge | null
): ((element: HTMLElement | null) => void) => {
  const store = useContext(StoreContext);
  const [element, setElement] = useState<HTMLElement | null>(null);

  useEffect(() => {
    if (!store || !element) return;
    const measure = () => {
      const box = element.getBoundingClientRect();
      store.set(id, edge, {
        x: box.left,
        y: box.top,
        width: box.width,
        height: box.height
      });
    };
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(element);
    window.addEventListener("resize", measure);
    return () => {
      observer.disconnect();
      window.removeEventListener("resize", measure);
      store.remove(id);
    };
  }, [store, element, id, edge]);

  return useCallback((node: HTMLElement | null) => setElement(node), []);
};

export const useEdgeInsets = (): EdgeInsets => useSnapshot().insets;

export const useFreeArea = (): Rect => useSnapshot().free;

export const useOverlayClip = (): string => useSnapshot().clip;

/** Every registered piece — for anything asking about the chrome itself. */
export const useChromeRects = (): readonly ChromeRect[] => useSnapshot().entries;
